/**
 * Embassy legalisation (รับรองเอกสารโดยสถานทูต) — cluster source of truth.
 *
 * Powers /embassy, /embassy/country/$country, /embassy/questions,
 * /embassy/q/$slug and /embassy/commercial.
 *
 * FACTUAL GUARDRAILS
 * - Thailand cannot issue an Apostille yet; the authoritative statement lives
 *   in `src/data/legal-facts.ts` (entry into force 28 Feb 2027). Never restate
 *   a date here.
 * - Government fees come from `src/data/official-fees.ts` only.
 * - Embassy fees and processing times are never quoted as fixed numbers —
 *   each mission sets and revises its own tariff.
 * - `hague` marks the DESTINATION country's membership of the 1961 Convention.
 *   It does not shorten the chain for Thai documents until the Convention is
 *   in force for Thailand.
 * - `presence: "verify"` is used wherever we are not certain a resident
 *   mission in Bangkok handles legalisation; the page then tells the reader
 *   to confirm with the mission instead of asserting.
 */

export type EmbassyPresence =
  /** Resident embassy/consular section in Bangkok. */
  | "bangkok"
  /** Handled by a mission accredited to Thailand from another country. */
  | "regional"
  /** Representative/trade office rather than a diplomatic mission. */
  | "office"
  /** Confirm current arrangements with the mission before planning. */
  | "verify";

export type EmbassyTarget = {
  slug: string;
  nameTh: string;
  nameEn: string;
  /** Country name only, used in copy. */
  countryTh: string;
  countryEn: string;
  region:
    | "asia-east"
    | "asia-south"
    | "asia-southeast"
    | "middle-east"
    | "europe"
    | "americas"
    | "oceania"
    | "africa";
  presence: EmbassyPresence;
  /** Destination country is a party to the 1961 Hague Apostille Convention. */
  hague: boolean;
  /** Language the mission normally requires for the translation. */
  langTh: string;
  /** Documents most frequently legalised for this destination. */
  docs: readonly string[];
  /** Destination-specific caution written as a condition, never a promise. */
  noteTh: string;
  /** Official reference for the reader to verify current requirements. */
  sourceLabel: string;
  sourceUrl: string;
};

/** Verification stamp for the directory as a whole. */
export const EMBASSY_REVIEWED_TH = "สิงหาคม 2569";
export const EMBASSY_REVIEWED_ISO = "2026-08-04";

const PERSONAL = [
  "สูติบัตร",
  "ทะเบียนบ้าน",
  "ทะเบียนสมรส / ทะเบียนหย่า",
  "ใบรับรองโสด",
  "ใบเปลี่ยนชื่อ-สกุล",
] as const;
const EDU = ["ทรานสคริปต์", "ปริญญาบัตร", "ใบรับรองสถานภาพนักศึกษา"] as const;
const CORP = [
  "หนังสือรับรองบริษัท (DBD)",
  "หนังสือมอบอำนาจ (POA)",
  "Commercial Invoice",
  "Certificate of Origin",
] as const;
const WORK = ["หนังสือรับรองความประพฤติ (PCC)", "ใบรับรองแพทย์", "ใบรับรองการทำงาน"] as const;

export const EMBASSY_TARGETS: readonly EmbassyTarget[] = [
  {
    slug: "china",
    nameTh: "สถานเอกอัครราชทูตสาธารณรัฐประชาชนจีน ประจำประเทศไทย",
    nameEn: "Embassy of the People's Republic of China in Thailand",
    countryTh: "จีน",
    countryEn: "China",
    region: "asia-east",
    presence: "bangkok",
    hague: true,
    langTh: "จีนตัวย่อ หรืออังกฤษ ตามที่หน่วยงานปลายทางกำหนด",
    docs: [...PERSONAL, ...CORP],
    noteTh:
      "งานยื่นจีนมักตรวจความต่อเนื่องของสายรับรองอย่างเคร่งครัด เอกสารต้องผ่านนิติกรณ์กรมการกงสุลก่อนเสมอ และคำแปลต้องสะกดชื่อตรงกับหนังสือเดินทางทุกตัวอักษร",
    sourceLabel: "สถานเอกอัครราชทูตจีนประจำประเทศไทย — งานกงสุล",
    sourceUrl: "http://th.china-embassy.gov.cn/",
  },
  {
    slug: "japan",
    nameTh: "สถานเอกอัครราชทูตญี่ปุ่น ประจำประเทศไทย",
    nameEn: "Embassy of Japan in Thailand",
    countryTh: "ญี่ปุ่น",
    countryEn: "Japan",
    region: "asia-east",
    presence: "bangkok",
    hague: true,
    langTh: "ญี่ปุ่นหรืออังกฤษ ตามที่หน่วยงานปลายทางกำหนด",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "หน่วยงานปลายทางในญี่ปุ่น (เขต/ศาลครอบครัว/สถานศึกษา) เป็นผู้กำหนดว่าต้องรับรองถึงชั้นไหน ควรขอเช็กลิสต์เป็นลายลักษณ์อักษรจากปลายทางก่อนเริ่มเดินเรื่อง",
    sourceLabel: "สถานเอกอัครราชทูตญี่ปุ่นประจำประเทศไทย",
    sourceUrl: "https://www.th.emb-japan.go.jp/",
  },
  {
    slug: "south-korea",
    nameTh: "สถานเอกอัครราชทูตสาธารณรัฐเกาหลี ประจำประเทศไทย",
    nameEn: "Embassy of the Republic of Korea in Thailand",
    countryTh: "เกาหลีใต้",
    countryEn: "South Korea",
    region: "asia-east",
    presence: "bangkok",
    hague: true,
    langTh: "เกาหลีหรืออังกฤษ ตามที่หน่วยงานปลายทางกำหนด",
    docs: [...PERSONAL, ...EDU, ...WORK],
    noteTh:
      "เอกสารทะเบียนราษฎรที่ยื่นเกาหลีมักถูกกำหนดอายุการคัดรับรองไม่เกิน 3–6 เดือน ควรคัดใหม่ก่อนแปลเสมอ",
    sourceLabel: "สถานเอกอัครราชทูตสาธารณรัฐเกาหลีประจำประเทศไทย",
    sourceUrl: "https://overseas.mofa.go.kr/th-th/index.do",
  },
  {
    slug: "taiwan",
    nameTh: "สำนักงานเศรษฐกิจและวัฒนธรรมไทเป ประจำประเทศไทย (TECO)",
    nameEn: "Taipei Economic and Cultural Office in Thailand",
    countryTh: "ไต้หวัน",
    countryEn: "Taiwan",
    region: "asia-east",
    presence: "office",
    hague: false,
    langTh: "จีนตัวเต็มหรืออังกฤษ",
    docs: [...PERSONAL, ...EDU, ...CORP],
    noteTh:
      "ไต้หวันไม่ได้เป็นภาคีอนุสัญญากรุงเฮก การรับรองจึงดำเนินการผ่านสำนักงานเศรษฐกิจและวัฒนธรรมไทเป ไม่ใช่สถานเอกอัครราชทูต",
    sourceLabel: "Taipei Economic and Cultural Office in Thailand",
    sourceUrl: "https://www.roc-taiwan.org/th_en/",
  },
  {
    slug: "vietnam",
    nameTh: "สถานเอกอัครราชทูตสาธารณรัฐสังคมนิยมเวียดนาม ประจำประเทศไทย",
    nameEn: "Embassy of Viet Nam in Thailand",
    countryTh: "เวียดนาม",
    countryEn: "Vietnam",
    region: "asia-southeast",
    presence: "bangkok",
    hague: false,
    langTh: "เวียดนามหรืออังกฤษ ตามที่หน่วยงานปลายทางกำหนด",
    docs: [...PERSONAL, ...CORP, ...WORK],
    noteTh:
      "เวียดนามยังไม่ใช่ภาคีอนุสัญญากรุงเฮก เอกสารไทยจึงต้องผ่านนิติกรณ์กรมการกงสุลแล้วจึงรับรองที่สถานทูตเวียดนาม",
    sourceLabel: "Embassy of Viet Nam in Thailand",
    sourceUrl: "https://vnembassy-bangkok.mofa.gov.vn/",
  },
  {
    slug: "cambodia",
    nameTh: "สถานเอกอัครราชทูตกัมพูชา ประจำประเทศไทย",
    nameEn: "Royal Embassy of Cambodia in Thailand",
    countryTh: "กัมพูชา",
    countryEn: "Cambodia",
    region: "asia-southeast",
    presence: "bangkok",
    hague: false,
    langTh: "เขมรหรืออังกฤษ",
    docs: [...PERSONAL, ...CORP],
    noteTh:
      "ควรยืนยันกับหน่วยงานปลายทางในกัมพูชาว่าต้องการคำแปลภาษาเขมรหรืออังกฤษ เพราะบางหน่วยงานรับเฉพาะเขมร",
    sourceLabel: "Royal Embassy of Cambodia in Bangkok",
    sourceUrl: "https://www.mfaic.gov.kh/",
  },
  {
    slug: "laos",
    nameTh: "สถานเอกอัครราชทูต สปป.ลาว ประจำประเทศไทย",
    nameEn: "Embassy of the Lao PDR in Thailand",
    countryTh: "ลาว",
    countryEn: "Laos",
    region: "asia-southeast",
    presence: "bangkok",
    hague: false,
    langTh: "ลาวหรืออังกฤษ",
    docs: [...PERSONAL, ...CORP],
    noteTh:
      "งานพาณิชย์ที่ยื่นลาวมักต้องผ่านหอการค้าไทยก่อนเข้าสายนิติกรณ์ ควรตรวจลำดับกับผู้รับปลายทางก่อน",
    sourceLabel: "Ministry of Foreign Affairs, Lao PDR",
    sourceUrl: "https://www.mofa.gov.la/",
  },
  {
    slug: "myanmar",
    nameTh: "สถานเอกอัครราชทูตเมียนมา ประจำประเทศไทย",
    nameEn: "Embassy of the Republic of the Union of Myanmar",
    countryTh: "เมียนมา",
    countryEn: "Myanmar",
    region: "asia-southeast",
    presence: "bangkok",
    hague: false,
    langTh: "อังกฤษ หรือเมียนมาตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...CORP],
    noteTh:
      "ระเบียบและช่วงเวลาเปิดรับงานกงสุลของเมียนมาปรับเปลี่ยนได้บ่อย โปรดยืนยันคิวและเอกสารก่อนเดินทางไปยื่น",
    sourceLabel: "Embassy of Myanmar, Bangkok",
    sourceUrl: "https://www.myanmarembassybkk.com/",
  },
  {
    slug: "malaysia",
    nameTh: "สถานเอกอัครราชทูตมาเลเซีย ประจำประเทศไทย",
    nameEn: "Embassy of Malaysia in Thailand",
    countryTh: "มาเลเซีย",
    countryEn: "Malaysia",
    region: "asia-southeast",
    presence: "bangkok",
    hague: false,
    langTh: "อังกฤษ หรือมลายูตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...CORP, ...WORK],
    noteTh:
      "มาเลเซียไม่ใช่ภาคีอนุสัญญากรุงเฮก จึงยังคงใช้สายรับรองเต็มรูปแบบ นิติกรณ์ → สถานทูต",
    sourceLabel: "Embassy of Malaysia, Bangkok",
    sourceUrl: "https://www.kln.gov.my/web/tha_bangkok/home",
  },
  {
    slug: "singapore",
    nameTh: "สถานเอกอัครราชทูตสิงคโปร์ ประจำประเทศไทย",
    nameEn: "Embassy of the Republic of Singapore",
    countryTh: "สิงคโปร์",
    countryEn: "Singapore",
    region: "asia-southeast",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษ",
    docs: [...PERSONAL, ...EDU, ...CORP],
    noteTh:
      "หน่วยงานปลายทางในสิงคโปร์จำนวนมากรับคำแปลภาษาอังกฤษที่ผ่านนิติกรณ์แล้วโดยไม่ต้องรับรองสถานทูตเพิ่ม ควรถามผู้รับก่อนเพื่อไม่เสียขั้นตอนเกินจำเป็น",
    sourceLabel: "Ministry of Foreign Affairs, Singapore",
    sourceUrl: "https://www.mfa.gov.sg/",
  },
  {
    slug: "indonesia",
    nameTh: "สถานเอกอัครราชทูตอินโดนีเซีย ประจำประเทศไทย",
    nameEn: "Embassy of the Republic of Indonesia",
    countryTh: "อินโดนีเซีย",
    countryEn: "Indonesia",
    region: "asia-southeast",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษ หรืออินโดนีเซียตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...CORP],
    noteTh:
      "เอกสารเพื่อการสมรสหรือการจ้างงานในอินโดนีเซียมักถูกกำหนดอายุเอกสารสั้น ควรวางแผนคิวรับรองให้จบภายในอายุที่ปลายทางกำหนด",
    sourceLabel: "Embassy of Indonesia, Bangkok",
    sourceUrl: "https://kemlu.go.id/bangkok",
  },
  {
    slug: "philippines",
    nameTh: "สถานเอกอัครราชทูตฟิลิปปินส์ ประจำประเทศไทย",
    nameEn: "Embassy of the Republic of the Philippines",
    countryTh: "ฟิลิปปินส์",
    countryEn: "Philippines",
    region: "asia-southeast",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษ",
    docs: [...PERSONAL, ...WORK],
    noteTh:
      "งานทะเบียนครอบครัวของฟิลิปปินส์มักต้องรายงานต่อหน่วยงานทะเบียนราษฎรปลายทางเพิ่มอีกขั้น ควรถามเช็กลิสต์จากปลายทางควบคู่กัน",
    sourceLabel: "Philippine Embassy in Bangkok",
    sourceUrl: "https://bangkokpe.dfa.gov.ph/",
  },
  {
    slug: "india",
    nameTh: "สถานเอกอัครราชทูตอินเดีย ประจำประเทศไทย",
    nameEn: "Embassy of India in Thailand",
    countryTh: "อินเดีย",
    countryEn: "India",
    region: "asia-south",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษ",
    docs: [...PERSONAL, ...EDU, ...CORP],
    noteTh:
      "งานยื่นอินเดียบางประเภทดำเนินการผ่านผู้ให้บริการที่สถานทูตกำหนด ควรตรวจช่องทางที่ถูกต้องก่อนยื่นเพื่อไม่ให้เอกสารถูกส่งคืน",
    sourceLabel: "Embassy of India, Bangkok",
    sourceUrl: "https://www.indianembassybangkok.gov.in/",
  },
  {
    slug: "pakistan",
    nameTh: "สถานเอกอัครราชทูตปากีสถาน ประจำประเทศไทย",
    nameEn: "Embassy of Pakistan in Thailand",
    countryTh: "ปากีสถาน",
    countryEn: "Pakistan",
    region: "asia-south",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษ",
    docs: [...PERSONAL, ...CORP],
    noteTh:
      "โปรดยืนยันกับฝ่ายกงสุลว่าต้องการต้นฉบับหรือสำเนาที่ผ่านนิติกรณ์ เพราะข้อกำหนดต่างกันตามประเภทเอกสาร",
    sourceLabel: "Embassy of Pakistan, Bangkok",
    sourceUrl: "https://mofa.gov.pk/thailand",
  },
  {
    slug: "bangladesh",
    nameTh: "สถานเอกอัครราชทูตบังกลาเทศ ประจำประเทศไทย",
    nameEn: "Embassy of Bangladesh in Thailand",
    countryTh: "บังกลาเทศ",
    countryEn: "Bangladesh",
    region: "asia-south",
    presence: "bangkok",
    hague: false,
    langTh: "อังกฤษ",
    docs: [...PERSONAL, ...CORP],
    noteTh:
      "งานพาณิชย์มักต้องมีหนังสือนำจากบริษัทผู้ส่งออกแนบไปพร้อมชุดเอกสารทุกครั้ง",
    sourceLabel: "Embassy of Bangladesh, Bangkok",
    sourceUrl: "https://bangkok.mofa.gov.bd/",
  },
  {
    slug: "united-arab-emirates",
    nameTh: "สถานเอกอัครราชทูตสหรัฐอาหรับเอมิเรตส์ ประจำประเทศไทย",
    nameEn: "Embassy of the United Arab Emirates",
    countryTh: "สหรัฐอาหรับเอมิเรตส์ (UAE)",
    countryEn: "United Arab Emirates",
    region: "middle-east",
    presence: "bangkok",
    hague: true,
    langTh: "อาหรับ หรืออังกฤษตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...EDU, ...CORP, ...WORK],
    noteTh:
      "งานใบอนุญาตทำงานใน UAE โดยทั่วไปต้องรับรองวุฒิการศึกษาให้ครบสาย และบางกรณีต้องแปลเป็นภาษาอาหรับโดยผู้แปลที่ปลายทางยอมรับหลังเอกสารถึง UAE แล้ว",
    sourceLabel: "Ministry of Foreign Affairs, UAE",
    sourceUrl: "https://www.mofa.gov.ae/en",
  },
  {
    slug: "saudi-arabia",
    nameTh: "สถานเอกอัครราชทูตซาอุดีอาระเบีย ประจำประเทศไทย",
    nameEn: "Royal Embassy of Saudi Arabia",
    countryTh: "ซาอุดีอาระเบีย",
    countryEn: "Saudi Arabia",
    region: "middle-east",
    presence: "bangkok",
    hague: true,
    langTh: "อาหรับ หรืออังกฤษตามที่ปลายทางกำหนด",
    docs: [...EDU, ...WORK, ...CORP],
    noteTh:
      "ชุดเอกสารแรงงานและวิชาชีพมักต้องผ่านการรับรองของหน่วยงานวิชาชีพในไทยก่อน แล้วจึงเข้าสายนิติกรณ์และสถานทูต",
    sourceLabel: "Ministry of Foreign Affairs, Saudi Arabia",
    sourceUrl: "https://www.mofa.gov.sa/en",
  },
  {
    slug: "qatar",
    nameTh: "สถานเอกอัครราชทูตกาตาร์ ประจำประเทศไทย",
    nameEn: "Embassy of the State of Qatar",
    countryTh: "กาตาร์",
    countryEn: "Qatar",
    region: "middle-east",
    presence: "bangkok",
    hague: false,
    langTh: "อาหรับ หรืออังกฤษตามที่ปลายทางกำหนด",
    docs: [...EDU, ...WORK, ...CORP],
    noteTh:
      "กาตาร์ยังไม่ใช่ภาคีอนุสัญญากรุงเฮก การรับรองจึงต้องเดินสายเต็มรูปแบบเสมอ",
    sourceLabel: "Ministry of Foreign Affairs, Qatar",
    sourceUrl: "https://www.mofa.gov.qa/en",
  },
  {
    slug: "kuwait",
    nameTh: "สถานเอกอัครราชทูตคูเวต ประจำประเทศไทย",
    nameEn: "Embassy of the State of Kuwait",
    countryTh: "คูเวต",
    countryEn: "Kuwait",
    region: "middle-east",
    presence: "bangkok",
    hague: false,
    langTh: "อาหรับ หรืออังกฤษตามที่ปลายทางกำหนด",
    docs: [...EDU, ...WORK],
    noteTh:
      "เอกสารด้านการจ้างงานมักถูกตรวจความสอดคล้องระหว่างชื่อในวุฒิ ใบรับรองงาน และหนังสือเดินทางอย่างละเอียด",
    sourceLabel: "Ministry of Foreign Affairs, Kuwait",
    sourceUrl: "https://www.mofa.gov.kw/en/",
  },
  {
    slug: "israel",
    nameTh: "สถานเอกอัครราชทูตอิสราเอล ประจำประเทศไทย",
    nameEn: "Embassy of Israel in Thailand",
    countryTh: "อิสราเอล",
    countryEn: "Israel",
    region: "middle-east",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษ หรือฮีบรูตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...WORK],
    noteTh:
      "เอกสารเพื่อการทำงานภาคเกษตรและงานทะเบียนครอบครัวมีเช็กลิสต์ต่างกันมาก ควรระบุวัตถุประสงค์ให้ชัดก่อนเริ่มเดินเรื่อง",
    sourceLabel: "Embassy of Israel in Thailand",
    sourceUrl: "https://embassies.gov.il/bangkok/",
  },
  {
    slug: "turkiye",
    nameTh: "สถานเอกอัครราชทูตตุรกี ประจำประเทศไทย",
    nameEn: "Embassy of the Republic of Türkiye",
    countryTh: "ตุรกี",
    countryEn: "Türkiye",
    region: "middle-east",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษ หรือตุรกีตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "หน่วยงานในตุรกีบางแห่งกำหนดให้แปลเป็นภาษาตุรกีโดยผู้แปลสาบานตนในตุรกีหลังเอกสารถึงปลายทาง",
    sourceLabel: "Republic of Türkiye Ministry of Foreign Affairs",
    sourceUrl: "https://www.mfa.gov.tr/default.en.mfa",
  },
  {
    slug: "united-states",
    nameTh: "สถานเอกอัครราชทูตสหรัฐอเมริกา ประจำประเทศไทย",
    nameEn: "Embassy of the United States of America",
    countryTh: "สหรัฐอเมริกา",
    countryEn: "United States",
    region: "americas",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษ",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "หน่วยงานสหรัฐฯ ส่วนใหญ่ เช่น USCIS รับคำแปลภาษาอังกฤษพร้อม Certificate of Accuracy โดยไม่ต้องผ่านสถานทูต ควรตรวจเช็กลิสต์ของหน่วยงานผู้รับก่อนเพื่อไม่ทำขั้นตอนเกินจำเป็น",
    sourceLabel: "U.S. Embassy Bangkok — U.S. Citizen Services",
    sourceUrl: "https://th.usembassy.gov/u-s-citizen-services/",
  },
  {
    slug: "canada",
    nameTh: "สถานเอกอัครราชทูตแคนาดา ประจำประเทศไทย",
    nameEn: "Embassy of Canada to Thailand",
    countryTh: "แคนาดา",
    countryEn: "Canada",
    region: "americas",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษ หรือฝรั่งเศส",
    docs: [...PERSONAL, ...EDU, ...WORK],
    noteTh:
      "งานคนเข้าเมืองแคนาดา (IRCC) ต้องการต้นฉบับพร้อมคำแปลที่รับรองความถูกต้องคู่กัน มากกว่าการรับรองที่สถานทูต",
    sourceLabel: "Government of Canada — Embassy in Thailand",
    sourceUrl: "https://www.international.gc.ca/country-pays/thailand-thailande/bangkok.aspx",
  },
  {
    slug: "brazil",
    nameTh: "สถานเอกอัครราชทูตบราซิล ประจำประเทศไทย",
    nameEn: "Embassy of Brazil in Bangkok",
    countryTh: "บราซิล",
    countryEn: "Brazil",
    region: "americas",
    presence: "bangkok",
    hague: true,
    langTh: "โปรตุเกส หรืออังกฤษตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...CORP],
    noteTh:
      "หน่วยงานบราซิลจำนวนมากกำหนดให้แปลเป็นภาษาโปรตุเกสโดยผู้แปลสาบานตนที่ขึ้นทะเบียนในบราซิล",
    sourceLabel: "Embassy of Brazil in Bangkok",
    sourceUrl: "https://www.gov.br/mre/pt-br/embaixada-banguecoque",
  },
  {
    slug: "mexico",
    nameTh: "สถานเอกอัครราชทูตเม็กซิโก ประจำประเทศไทย",
    nameEn: "Embassy of Mexico in Thailand",
    countryTh: "เม็กซิโก",
    countryEn: "Mexico",
    region: "americas",
    presence: "bangkok",
    hague: true,
    langTh: "สเปน หรืออังกฤษตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...CORP],
    noteTh:
      "ปลายทางในเม็กซิโกมักขอคำแปลภาษาสเปนจากผู้แปลที่ศาลท้องถิ่นรับรอง ควรตรวจก่อนแปลในไทย",
    sourceLabel: "Embassy of Mexico in Thailand",
    sourceUrl: "https://embamex.sre.gob.mx/tailandia/",
  },
  {
    slug: "united-kingdom",
    nameTh: "สถานเอกอัครราชทูตสหราชอาณาจักร ประจำประเทศไทย",
    nameEn: "British Embassy Bangkok",
    countryTh: "สหราชอาณาจักร",
    countryEn: "United Kingdom",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษ",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "งานวีซ่าสหราชอาณาจักรโดยทั่วไปรับคำแปลที่มีคำรับรองของผู้แปล ชื่อผู้แปล วันที่ และข้อมูลติดต่อ โดยไม่ต้องรับรองสถานทูต",
    sourceLabel: "GOV.UK — British Embassy Bangkok",
    sourceUrl: "https://www.gov.uk/world/organisations/british-embassy-bangkok",
  },
  {
    slug: "germany",
    nameTh: "สถานเอกอัครราชทูตเยอรมนี ประจำประเทศไทย",
    nameEn: "German Embassy Bangkok",
    countryTh: "เยอรมนี",
    countryEn: "Germany",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "เยอรมัน (beglaubigte Übersetzung) ตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "งานทะเบียนครอบครัวของเยอรมนีมักผ่านขั้นตอนตรวจสอบเอกสารของฝ่ายกงสุลก่อน ซึ่งใช้เวลานานกว่างานทั่วไปมาก ควรเผื่อเวลาตั้งแต่ต้น",
    sourceLabel: "German Embassy Bangkok",
    sourceUrl: "https://bangkok.diplo.de/",
  },
  {
    slug: "france",
    nameTh: "สถานเอกอัครราชทูตฝรั่งเศส ประจำประเทศไทย",
    nameEn: "Embassy of France in Thailand",
    countryTh: "ฝรั่งเศส",
    countryEn: "France",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "ฝรั่งเศส (traduction assermentée) ตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "ฝรั่งเศสมักกำหนดให้ใช้ผู้แปลที่อยู่ในรายชื่อที่สถานทูตหรือศาลฝรั่งเศสยอมรับ รายชื่อของสถานทูตหนึ่งใช้ข้ามสถานทูตอื่นไม่ได้",
    sourceLabel: "Ambassade de France en Thaïlande",
    sourceUrl: "https://th.ambafrance.org/",
  },
  {
    slug: "italy",
    nameTh: "สถานเอกอัครราชทูตอิตาลี ประจำประเทศไทย",
    nameEn: "Embassy of Italy in Bangkok",
    countryTh: "อิตาลี",
    countryEn: "Italy",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "อิตาลี ตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "อิตาลีมักกำหนดขั้นตอน dichiarazione di valore สำหรับวุฒิการศึกษา ซึ่งเป็นคนละขั้นกับการรับรองคำแปล",
    sourceLabel: "Ambasciata d'Italia Bangkok",
    sourceUrl: "https://ambbangkok.esteri.it/",
  },
  {
    slug: "spain",
    nameTh: "สถานเอกอัครราชทูตสเปน ประจำประเทศไทย",
    nameEn: "Embassy of Spain in Thailand",
    countryTh: "สเปน",
    countryEn: "Spain",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "สเปน (traductor jurado) ตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "หน่วยงานสเปนหลายแห่งรับเฉพาะคำแปลจากผู้แปลสาบานตนที่ขึ้นทะเบียนกับกระทรวงการต่างประเทศสเปน",
    sourceLabel: "Embajada de España en Tailandia",
    sourceUrl: "https://www.exteriores.gob.es/Embajadas/bangkok/",
  },
  {
    slug: "netherlands",
    nameTh: "สถานเอกอัครราชทูตเนเธอร์แลนด์ ประจำประเทศไทย",
    nameEn: "Embassy of the Kingdom of the Netherlands",
    countryTh: "เนเธอร์แลนด์",
    countryEn: "Netherlands",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษหรือดัตช์ ตามที่ปลายทางกำหนด",
    docs: [...PERSONAL],
    noteTh:
      "งานทะเบียนราษฎรของเนเธอร์แลนด์ตรวจความถูกต้องของชื่อและวันเดือนปีเกิดอย่างเข้มงวด ควรตรวจการทับศัพท์ให้ตรงกับหนังสือเดินทาง",
    sourceLabel: "Netherlands Embassy in Bangkok",
    sourceUrl: "https://www.netherlandsworldwide.nl/countries/thailand",
  },
  {
    slug: "belgium",
    nameTh: "สถานเอกอัครราชทูตเบลเยียม ประจำประเทศไทย",
    nameEn: "Embassy of Belgium in Thailand",
    countryTh: "เบลเยียม",
    countryEn: "Belgium",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "ดัตช์ ฝรั่งเศส หรือเยอรมัน ตามภูมิภาคปลายทาง",
    docs: [...PERSONAL],
    noteTh:
      "ภาษาที่ต้องใช้ขึ้นกับภูมิภาคของเทศบาลปลายทางในเบลเยียม ควรถามเทศบาลก่อนสั่งแปล",
    sourceLabel: "Belgium in Thailand",
    sourceUrl: "https://thailand.diplomatie.belgium.be/",
  },
  {
    slug: "switzerland",
    nameTh: "สถานเอกอัครราชทูตสวิตเซอร์แลนด์ ประจำประเทศไทย",
    nameEn: "Embassy of Switzerland in Thailand",
    countryTh: "สวิตเซอร์แลนด์",
    countryEn: "Switzerland",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "เยอรมัน ฝรั่งเศส หรืออิตาลี ตามรัฐปลายทาง",
    docs: [...PERSONAL, ...WORK],
    noteTh:
      "ข้อกำหนดต่างกันตามรัฐ (canton) ควรขอเช็กลิสต์จากสำนักทะเบียนของรัฐปลายทางโดยตรง",
    sourceLabel: "Embassy of Switzerland in Thailand",
    sourceUrl: "https://www.eda.admin.ch/bangkok",
  },
  {
    slug: "austria",
    nameTh: "สถานเอกอัครราชทูตออสเตรีย ประจำประเทศไทย",
    nameEn: "Austrian Embassy Bangkok",
    countryTh: "ออสเตรีย",
    countryEn: "Austria",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "เยอรมัน ตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "เอกสารทะเบียนราษฎรมักถูกกำหนดอายุการคัดรับรอง ควรคัดใหม่ก่อนเข้าสายรับรอง",
    sourceLabel: "Austrian Embassy Bangkok",
    sourceUrl: "https://www.bmeia.gv.at/en/austrian-embassy-bangkok",
  },
  {
    slug: "sweden",
    nameTh: "สถานเอกอัครราชทูตสวีเดน ประจำประเทศไทย",
    nameEn: "Embassy of Sweden in Bangkok",
    countryTh: "สวีเดน",
    countryEn: "Sweden",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษหรือสวีเดน ตามที่ปลายทางกำหนด",
    docs: [...PERSONAL],
    noteTh:
      "งานย้ายถิ่นฐานของสวีเดนดำเนินการผ่านหน่วยงานคนเข้าเมืองเป็นหลัก ควรตรวจว่าจำเป็นต้องรับรองสถานทูตจริงหรือไม่",
    sourceLabel: "Sweden Abroad — Bangkok",
    sourceUrl: "https://www.swedenabroad.se/en/embassies/thailand-bangkok/",
  },
  {
    slug: "denmark",
    nameTh: "สถานเอกอัครราชทูตเดนมาร์ก ประจำประเทศไทย",
    nameEn: "Royal Danish Embassy in Bangkok",
    countryTh: "เดนมาร์ก",
    countryEn: "Denmark",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษหรือเดนมาร์ก ตามที่ปลายทางกำหนด",
    docs: [...PERSONAL],
    noteTh:
      "งานสมรสและครอบครัวของเดนมาร์กมักมีแบบฟอร์มเฉพาะที่ต้องกรอกคู่กับเอกสารรับรอง",
    sourceLabel: "Royal Danish Embassy in Bangkok",
    sourceUrl: "https://thailand.um.dk/",
  },
  {
    slug: "norway",
    nameTh: "สถานเอกอัครราชทูตนอร์เวย์ ประจำประเทศไทย",
    nameEn: "Royal Norwegian Embassy in Bangkok",
    countryTh: "นอร์เวย์",
    countryEn: "Norway",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษหรือนอร์เวย์ ตามที่ปลายทางกำหนด",
    docs: [...PERSONAL],
    noteTh:
      "ควรตรวจสอบว่าหน่วยงานปลายทางต้องการเอกสารต้นฉบับหรือสำเนารับรอง เพราะมีผลต่อการวางแผนคัดเอกสาร",
    sourceLabel: "Norway in Thailand",
    sourceUrl: "https://www.norway.no/en/thailand/",
  },
  {
    slug: "finland",
    nameTh: "สถานเอกอัครราชทูตฟินแลนด์ ประจำประเทศไทย",
    nameEn: "Embassy of Finland, Bangkok",
    countryTh: "ฟินแลนด์",
    countryEn: "Finland",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษ ฟินแลนด์ หรือสวีเดน ตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "ควรยืนยันกับหน่วยงานปลายทางว่ารับคำแปลภาษาอังกฤษได้หรือไม่ก่อนสั่งแปลภาษาท้องถิ่น",
    sourceLabel: "Finland Abroad — Thailand",
    sourceUrl: "https://finlandabroad.fi/web/tha/frontpage",
  },
  {
    slug: "poland",
    nameTh: "สถานเอกอัครราชทูตโปแลนด์ ประจำประเทศไทย",
    nameEn: "Embassy of the Republic of Poland in Bangkok",
    countryTh: "โปแลนด์",
    countryEn: "Poland",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "โปแลนด์ ตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "หน่วยงานโปแลนด์มักกำหนดให้ใช้ผู้แปลสาบานตนที่ขึ้นทะเบียนในโปแลนด์สำหรับเอกสารทางการ",
    sourceLabel: "Embassy of Poland in Bangkok",
    sourceUrl: "https://www.gov.pl/web/thailand",
  },
  {
    slug: "czech-republic",
    nameTh: "สถานเอกอัครราชทูตสาธารณรัฐเช็ก ประจำประเทศไทย",
    nameEn: "Embassy of the Czech Republic in Bangkok",
    countryTh: "สาธารณรัฐเช็ก",
    countryEn: "Czech Republic",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "เช็ก ตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "ปลายทางในเช็กมักขอคำแปลจากผู้แปลที่ศาลเช็กแต่งตั้ง ควรวางแผนว่าจะแปลในไทยหรือปลายทาง",
    sourceLabel: "Embassy of the Czech Republic in Bangkok",
    sourceUrl: "https://mzv.gov.cz/bangkok",
  },
  {
    slug: "russia",
    nameTh: "สถานเอกอัครราชทูตสหพันธรัฐรัสเซีย ประจำประเทศไทย",
    nameEn: "Embassy of the Russian Federation in Thailand",
    countryTh: "รัสเซีย",
    countryEn: "Russia",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "รัสเซีย ตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "เอกสารที่ใช้ในรัสเซียมักต้องแปลเป็นภาษารัสเซียและรับรองโดยโนตารีในรัสเซียอีกชั้นหลังเอกสารถึงปลายทาง",
    sourceLabel: "Embassy of the Russian Federation in Thailand",
    sourceUrl: "https://thailand.mid.ru/",
  },
  {
    slug: "australia",
    nameTh: "สถานเอกอัครราชทูตออสเตรเลีย ประจำประเทศไทย",
    nameEn: "Australian Embassy Thailand",
    countryTh: "ออสเตรเลีย",
    countryEn: "Australia",
    region: "oceania",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษ (คำแปลโดยนักแปล NAATI)",
    docs: [...PERSONAL, ...EDU, ...WORK],
    noteTh:
      "งานยื่นหน่วยงานออสเตรเลียโดยทั่วไปใช้คำแปล NAATI พร้อมตราและหมายเลข CPN ไม่ใช่การรับรองผ่านกรมการกงสุลหรือสถานทูต",
    sourceLabel: "Australian Embassy Thailand",
    sourceUrl: "https://thailand.embassy.gov.au/",
  },
  {
    slug: "new-zealand",
    nameTh: "สถานเอกอัครราชทูตนิวซีแลนด์ ประจำประเทศไทย",
    nameEn: "New Zealand Embassy Bangkok",
    countryTh: "นิวซีแลนด์",
    countryEn: "New Zealand",
    region: "oceania",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษ (ผู้แปลที่ INZ ยอมรับ)",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "หน่วยงานคนเข้าเมืองนิวซีแลนด์ไม่รับคำแปลที่ผู้ยื่นแปลเอง ต้องเป็นผู้แปลอิสระที่ระบุคุณสมบัติได้",
    sourceLabel: "New Zealand Embassy Bangkok",
    sourceUrl: "https://www.mfat.govt.nz/en/countries-and-regions/south-east-asia/thailand/",
  },
  {
    slug: "south-africa",
    nameTh: "สถานเอกอัครราชทูตแอฟริกาใต้ ประจำประเทศไทย",
    nameEn: "Embassy of South Africa in Thailand",
    countryTh: "แอฟริกาใต้",
    countryEn: "South Africa",
    region: "africa",
    presence: "bangkok",
    hague: true,
    langTh: "อังกฤษ",
    docs: [...PERSONAL, ...CORP],
    noteTh:
      "ควรยืนยันกับฝ่ายกงสุลว่าปลายทางต้องการรับรองแบบใด เพราะข้อกำหนดของหน่วยงานภายในต่างกัน",
    sourceLabel: "Department of International Relations, South Africa",
    sourceUrl: "https://www.dirco.gov.za/",
  },
  {
    slug: "egypt",
    nameTh: "สถานเอกอัครราชทูตอียิปต์ ประจำประเทศไทย",
    nameEn: "Embassy of the Arab Republic of Egypt",
    countryTh: "อียิปต์",
    countryEn: "Egypt",
    region: "africa",
    presence: "bangkok",
    hague: false,
    langTh: "อาหรับ หรืออังกฤษตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "อียิปต์ยังไม่ใช่ภาคีอนุสัญญากรุงเฮก จึงต้องรับรองครบสายทั้งนิติกรณ์และสถานทูตเสมอ",
    sourceLabel: "Ministry of Foreign Affairs, Egypt",
    sourceUrl: "https://www.mfa.gov.eg/english",
  },
  {
    slug: "ireland",
    nameTh: "สถานเอกอัครราชทูตไอร์แลนด์ ประจำประเทศไทย",
    nameEn: "Embassy of Ireland in Thailand",
    countryTh: "ไอร์แลนด์",
    countryEn: "Ireland",
    region: "europe",
    presence: "verify",
    hague: true,
    langTh: "อังกฤษ",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "ควรตรวจสอบกับคณะผู้แทนที่ดูแลประเทศไทยว่าให้บริการรับรองเอกสารประเภทใดบ้าง เพราะบางงานส่งกลับไปดำเนินการที่ประเทศต้นทาง",
    sourceLabel: "Department of Foreign Affairs, Ireland",
    sourceUrl: "https://www.ireland.ie/en/thailand/bangkok/",
  },
  {
    slug: "portugal",
    nameTh: "สถานเอกอัครราชทูตโปรตุเกส ประจำประเทศไทย",
    nameEn: "Embassy of Portugal in Bangkok",
    countryTh: "โปรตุเกส",
    countryEn: "Portugal",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "โปรตุเกส ตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "งานวีซ่าถิ่นที่อยู่และงานทะเบียนของโปรตุเกสมีเช็กลิสต์แยกกัน ควรระบุวัตถุประสงค์ให้ชัดตั้งแต่ต้น",
    sourceLabel: "Portugal — Embassy in Bangkok",
    sourceUrl: "https://www.bangkok.embaixadaportugal.mne.gov.pt/",
  },
  {
    slug: "greece",
    nameTh: "สถานเอกอัครราชทูตกรีซ ประจำประเทศไทย",
    nameEn: "Embassy of Greece in Bangkok",
    countryTh: "กรีซ",
    countryEn: "Greece",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "กรีกหรืออังกฤษ ตามที่ปลายทางกำหนด",
    docs: [...PERSONAL],
    noteTh:
      "ควรตรวจกับหน่วยงานปลายทางว่าต้องการคำแปลภาษากรีกจากผู้แปลที่รับรองในกรีซหรือไม่",
    sourceLabel: "Embassy of Greece in Bangkok",
    sourceUrl: "https://www.mfa.gr/missionsabroad/en/thailand-en/",
  },
  {
    slug: "hungary",
    nameTh: "สถานเอกอัครราชทูตฮังการี ประจำประเทศไทย",
    nameEn: "Embassy of Hungary in Bangkok",
    countryTh: "ฮังการี",
    countryEn: "Hungary",
    region: "europe",
    presence: "bangkok",
    hague: true,
    langTh: "ฮังการี ตามที่ปลายทางกำหนด",
    docs: [...PERSONAL, ...EDU],
    noteTh:
      "งานเอกสารทางการของฮังการีมักผูกกับหน่วยงานแปลของรัฐที่ปลายทาง ควรตรวจก่อนสั่งแปลในไทย",
    sourceLabel: "Embassy of Hungary in Bangkok",
    sourceUrl: "https://bangkok.mfa.gov.hu/",
  },
] as const;

export const EMBASSY_BY_SLUG: Record<string, EmbassyTarget> = Object.fromEntries(
  EMBASSY_TARGETS.map((e) => [e.slug, e]),
);

export const EMBASSY_REGIONS: Record<EmbassyTarget["region"], string> = {
  "asia-east": "เอเชียตะวันออก",
  "asia-south": "เอเชียใต้",
  "asia-southeast": "เอเชียตะวันออกเฉียงใต้",
  "middle-east": "ตะวันออกกลาง",
  europe: "ยุโรป",
  americas: "อเมริกา",
  oceania: "โอเชียเนีย",
  africa: "แอฟริกา",
};

export const EMBASSY_PRESENCE_LABEL: Record<EmbassyPresence, string> = {
  bangkok: "มีคณะผู้แทนในกรุงเทพฯ",
  regional: "ดูแลประเทศไทยจากคณะผู้แทนในประเทศใกล้เคียง",
  office: "ดำเนินการผ่านสำนักงานผู้แทน ไม่ใช่สถานเอกอัครราชทูต",
  verify: "โปรดตรวจสอบขอบเขตงานกงสุลกับคณะผู้แทนก่อนวางแผน",
};

/** Chain steps rendered as HowTo on every embassy page. */
export const EMBASSY_CHAIN_STEPS: readonly { title: string; detail: string }[] = [
  {
    title: "ตรวจเช็กลิสต์ของหน่วยงานผู้รับปลายทาง",
    detail:
      "ผู้ตัดสินว่าเอกสารต้องรับรองถึงชั้นไหนคือหน่วยงานที่รับเอกสารปลายทาง ไม่ใช่ผู้แปลหรือผู้เดินเรื่อง ขอเช็กลิสต์เป็นลายลักษณ์อักษรก่อนเริ่มเสมอ",
  },
  {
    title: "คัดต้นฉบับใหม่จากหน่วยงานผู้ออกเอกสาร",
    detail:
      "เอกสารทะเบียนราษฎรมักถูกกำหนดอายุการคัดรับรอง การใช้สำเนาเก่าเป็นสาเหตุการถูกตีกลับที่พบบ่อยที่สุดกรณีหนึ่ง",
  },
  {
    title: "แปลโดยผู้แปลที่ปลายทางยอมรับ แล้วยื่นนิติกรณ์ที่กรมการกงสุล",
    detail:
      "เอกสารเอกชนต้องผ่านทนายความผู้ทำคำรับรองลายมือชื่อและเอกสาร (Notarial Services Attorney) ก่อน จากนั้นจึงยื่นรับรองที่กองสัญชาติและนิติกรณ์ กรมการกงสุล",
  },
  {
    title: "ยื่นรับรองที่สถานทูตหรือคณะผู้แทนของประเทศปลายทาง",
    detail:
      "แต่ละคณะผู้แทนกำหนดแบบฟอร์ม ภาษา ค่าธรรมเนียม และรอบเวลารับ-คืนเอกสารของตนเอง ต้องยึดประกาศล่าสุดของคณะผู้แทนนั้นเป็นหลัก",
  },
];

/** Advisory content reused across the cluster (A5 standard, >= 5 items each). */
export const EMBASSY_TIPS: readonly string[] = [
  "ขอเช็กลิสต์เป็นลายลักษณ์อักษรจากหน่วยงานผู้รับปลายทางก่อนเริ่มแปล เพื่อกำหนดว่าต้องรับรองถึงชั้นไหนและใช้ภาษาใด",
  "คัดต้นฉบับทะเบียนราษฎรใหม่ก่อนแปล และตรวจว่าปลายทางกำหนดอายุเอกสารไว้กี่เดือน",
  "สะกดชื่อ-นามสกุลในคำแปลให้ตรงกับหนังสือเดินทางทุกตัวอักษร รวมถึงชื่อกลางและคำนำหน้า",
  "ถ่ายสำเนาทุกหน้าที่มีตราประทับ ลายเซ็น และข้อความด้านหลัง แล้วแปลให้ครบทุกหน้า",
  "วางแผนคิวรวมทั้งสาย (Notary → นิติกรณ์ → สถานทูต) เผื่อวันหยุดของทั้งสองประเทศและรอบรับ-คืนเอกสารของคณะผู้แทน",
  "เก็บสำเนาชุดที่รับรองแล้วไว้อย่างน้อยหนึ่งชุด เผื่อปลายทางเรียกเอกสารเพิ่มภายหลัง",
];

export const EMBASSY_WARNINGS: readonly string[] = [
  "ใช้คำแปลที่ผู้ยื่นแปลเอง หรือผู้แปลที่ปลายทางไม่ยอมรับ — เป็นสาเหตุการถูกตีกลับที่พบมากที่สุด",
  "ข้ามขั้นตอนนิติกรณ์ของกรมการกงสุลแล้วนำเอกสารไปยื่นสถานทูตโดยตรง สถานทูตส่วนใหญ่จะไม่รับ",
  "ยื่นออสเตรเลียหรือนิวซีแลนด์ด้วยคำแปลที่ผ่านกงสุล ทั้งที่หน่วยงานปลายทางกำหนดคำแปล NAATI หรือผู้แปลที่ INZ ยอมรับ",
  "ใช้รายชื่อผู้แปลสาบานตนของสถานทูตหนึ่งไปยื่นอีกสถานทูตหนึ่งในกลุ่มเชงเก้น ซึ่งใช้ข้ามกันไม่ได้",
  "แปลไว้ล่วงหน้านานเกินไปจนต้นฉบับหมดอายุระหว่างรอคิวคณะผู้แทน",
  "อ้างอิงค่าธรรมเนียมหรือระยะเวลาของคณะผู้แทนจากข้อมูลเก่า ทั้งที่แต่ละแห่งปรับอัตราและรอบเวลาได้โดยไม่แจ้งล่วงหน้า",
];

/** Commercial-document track (Board of Trade / chamber → MFA → embassy). */
export const EMBASSY_COMMERCIAL_DOCS: readonly { name: string; note: string }[] = [
  { name: "Commercial Invoice", note: "ผู้ซื้อหรือธนาคารปลายทางเป็นผู้กำหนดว่าต้องรับรองถึงชั้นใด" },
  { name: "Packing List", note: "มักยื่นควบคู่กับ Invoice ในชุดเดียวกัน" },
  { name: "Certificate of Origin", note: "ออกหรือรับรองโดยหน่วยงานที่ประเทศปลายทางยอมรับ" },
  { name: "หนังสือรับรองบริษัท (DBD)", note: "ควรคัดใหม่ก่อนเข้าสายรับรองเพราะมักถูกกำหนดอายุ" },
  { name: "Board Resolution / หนังสือมอบอำนาจ", note: "เป็นเอกสารเอกชน ต้องผ่านการรับรองลายมือชื่อก่อน" },
  { name: "Free Sale Certificate", note: "ใช้กับสินค้าที่ปลายทางกำหนดให้แสดงสถานะการจำหน่ายในประเทศผู้ผลิต" },
  { name: "Health / Analysis Certificate", note: "ออกโดยหน่วยงานเฉพาะทาง ต้องตรวจว่าปลายทางรับรูปแบบใด" },
];
